import type {
  TBool,
  TSubject,
  TYearPeriod,
  TChildrenGrade,
  TAvailablePlatform,
  IPaginationResponse,
  TGameplayModuleState
} from './common'

import type { TRouteLabels, TGradePeriods, TModuleLabels } from './labels'
import type { TModules, TModuleImages, TRouteModules, TMinimalModules, TInsertModuleTranslations } from './modules'

// Az egy útvonalokhoz tartozó egyéb beállítások, igaz-hamis állapotok.
// NOTE: szándékosan nem tömb-alapú típussal foglalkozunk itt.
export enum ERouteOptionFlag {
  Generated = 1,
  Editable  = 2,
  Showable  = 4
  //  ...   = 8,
  //  ...   = 16
}

/** A statService által, útvonalak lokalizálására használt adatstruktúra. */
export interface IRouteDetails {
  /** Útvonalazonosító. */
  readonly id: number
  /** Az útvonal neve.  */
  readonly name: string
  /** A benne lévő modulok sorrendben. */
  readonly moduleIds: readonly number[]
  /** Szintfelmérő esetében melyik szintfelmérő. */
  readonly subject: TSubject | null
  /** Témacímkék azonosítói. */
  readonly themeIds: readonly number[]
  /** Elérhetőségi flagek. */
  readonly availabilityFlags: number
  /** A létrehozó felhasználó azonsítója. */
  readonly userIdentifier: string | null
  /** Útvonalcímkék azonosítói. */
  readonly labelIds: readonly number[]
}

export type TRouteDetails = readonly IRouteDetails[]

export interface IGetRoutesDetailsByIdsRequest {
  readonly ids: readonly number[]
  readonly platform: TAvailablePlatform
}

export interface IGetRoutesDetailsByIdsResponse {
  readonly routesDetails: TRouteDetails
}

/**
 * @deprecated A hozzátartozó route meg fog szűnni.
 * @see IGetRoutesDetailsByIdsRequest
 */
export interface ICreateRouteByThemeAndGradeRequest {
  readonly themeLabelId: number
  readonly grade: TChildrenGrade
}

/**
 * @deprecated A hozzátartozó route meg fog szűnni.
 * @see IGetRoutesDetailsByIdsResponse
 */
export interface ICreateRouteByThemeAndGradeResponse {
  readonly routeId: number
}

export interface IBaseRoute {
  /** Útvonal megnevezése. */
  readonly name: string
  /** Leírás. */
  readonly description: string
  /** A tantárgy, szintfelmérőrő esetén. */
  readonly subject: TSubject | null
}

export interface IUpdateRoute extends IBaseRoute {
  /** Állapotokat tartalmazó flag. */
  readonly optionFlags: number
}

export interface IInsertRoute extends IUpdateRoute {
  /** A létrehozó felhasználó azonosítója, HA egyéni útvonalról van szó. */
  readonly userIdentifier: string | null
  /** Azon útvonal azonosítója, amely alapján létrehoztuk az útvonalat, ha van olyan. */
  readonly parentRouteId: number | null
  /** Az útvonal elérhetőségi flagje. */
  readonly availabilityFlags: number
  /** Az útvonalban lévő modulok száma. */
  readonly moduleCount: number
}

export interface IRoute extends IInsertRoute {
  readonly id: number
  readonly createdAt: string

  /** Egyéb olyan mezők, amelyek mesterségesen jönnek létre lekérdezés során. */
  readonly isGenerated: TBool
  readonly isEditable: TBool
  readonly isShowable: TBool
}

export type TRoutes = readonly IRoute[]

export interface ILevelCheckerRoute extends IRoute {
  /** Az adott szintfelmérő melyik korosztályhoz tartozik. */
  readonly grade: TChildrenGrade
}

/** Szintfelmérő útvonalak. */
export type TLevelCheckerRoutes = readonly ILevelCheckerRoute[]

export interface IMinimalLevelCheckerRoute {
  /** Egyedi azonosító. */
  readonly id: number
  /** Az útvonal neve. */
  readonly name: string
  /** Az évfolyam ahol az útvonal releváns. */
  readonly grade: TChildrenGrade
  /** A tantárgy amelyikre vonatkozik. */
  readonly subject: TSubject
}

export type TMinimalLevelCheckerRoutes = readonly IMinimalLevelCheckerRoute[]

export interface ILevelCheckerRouteFilter {
  /** A lehetséges témakörönkénti és életkor szerinti szintfelmérők listája. */
  readonly routeFilters: TReadonlyRecord<TSubject, TReadonlyRecord<TChildrenGrade, TMinimalLevelCheckerRoutes>>

  /**
   * A lekérdezés állapotában a legközelebbi dátumpont, amelyhez tartozik szintefelmérő.
   * Ez szükséges, mert a frontenden lévő szövegezés ettől is függ.
   */
  readonly closestPeriod: TYearPeriod
}

export interface IGetLevelCheckerRoutesResponse {
  readonly routes: TLevelCheckerRoutes
}

export interface IRouteWithModuleCount extends IRoute {}

export type TRoutesWithModuleCounts = readonly IRouteWithModuleCount[]

export interface IDigestRoute extends IRoute {
  readonly gradePeriods: TGradePeriods
}

export interface IGetRouteByIdResponse {
  readonly route: IDigestRoute
}

export interface IGetLevelCheckersByGradePeriodRequest {
  readonly grade: TChildrenGrade
  readonly period: TYearPeriod
}

export interface IGetLevelCheckersByGradePeriodResponse {
  readonly routes: TRoutes
}

export interface IInsertNewLevelChecker extends IBaseRoute {
  /** Azon platformok, ahol az adott útvonal elérhető. */
  readonly platforms: TAvailablePlatform[]
  /** Évfolyam-időszak címkeazonosítók tömbje. */
  readonly labelIds: readonly number[]
}

export interface IInsertLevelCheckerRequest {
  /** A benne lévő modulok azonosítói, sorrendben. Minden esetben kötelező jelleggel! */
  readonly moduleIds: readonly (number | null)[]
  /**
   * Melyik meglévő útvonalat szeretnénk „felülcsapni”.
   * Abban az esetben, ha ez meg van adva, akkor mindenfélképpen ez a mérvadó.
   */
  readonly parentRouteId: number | null

  /** Teljesen új útvonal felvétele esetén. */
  readonly routeDetails: IInsertNewLevelChecker | null
}

export interface IHasGeneratedRouteResponse {
  /** Megmondja, hogy egy adott felhasználó generált-e már AI-val útvonalat. */
  readonly hasGenerated: boolean
}

/** Tantárgy és évfolyamidőszak szerinti szintfelmérő lekérdezés. */
export interface IGetLevelCheckerParams {
  readonly subject: TSubject
  readonly gradePeriodId: number
}

export interface IGetMatchingLevelCheckerResponse {
  /** Az adott szintfelmérőhöz melyik útvonalazonosító tartozik. */
  readonly id: number | null
}

export interface IGetDigestRouteById extends IRoute {
  readonly isLevelChecker: boolean
  readonly modules: TModules
}

export interface IGetUserRoutesResponse extends IPaginationResponse<TRoutes> {}

/** Felhasználók útvonalaival kapcsolatos típusok. */
export interface IInsertUserRouteRequest {
  readonly name: string
  readonly moduleIds: readonly (number | null)[]
  readonly parentRouteId: number | null
}

export interface IUpdateUserRouteRequest extends IInsertUserRouteRequest {}

export interface IInsertUserRouteResponse {
  readonly routeId: number
}

export interface ISetRouteNotEditableRequest {
  readonly routeIds: readonly number[]
}

export interface IDeleteUserRoutesByIdsRequest {
  readonly routeIds: readonly number[]
}

/** Élménytér inicializálásához használt típusok. */
export interface IExperienceSpaceModuleInfo {
  readonly moduleId: number
  readonly state: TGameplayModuleState
  readonly appSerial: string
}

export type TExperienceSpaceModuleInfos = readonly IExperienceSpaceModuleInfo[]

export interface IExperienceSpaceInitResponse {
  /** Az adott útvonalban szereplő témák azonosítóinak tömbje. */
  readonly themeIds: readonly number[]
  /** Szintelmérő-e az adott útvonal. */
  readonly isLevelChecker: boolean
  /** Az útvonalban lévő modulok információi. */
  readonly modules: readonly IExperienceSpaceModuleInfo[]
}

export interface IMinimalRouteDetails {
  readonly id: number
  readonly moduleCount: number
  readonly name: string
  readonly subject: TSubject | null
  readonly userIdentifier: string | null
  readonly availabilityFlags: number
  readonly themeLabelId: number | null
}

export interface IGetRoutesDetailsResponse {
  /** Minimális útvonalinformációk. */
  readonly routes: readonly IMinimalRouteDetails[]
  /** Az összes, releváns útvonal-címke páros. */
  readonly routeLabels: TRouteLabels
  /** Az összes, releváns útvonal-modul páros. */
  readonly routeModules: TRouteModules
  /** Az érintett modulokkal kapcsolatos minimális információk. */
  readonly modules: TMinimalModules
  /** Az érintett modulok fordításai. */
  readonly moduleTranslations: TInsertModuleTranslations
  /** Az érintett modulok képei. */
  readonly moduleImages: TModuleImages
  /** Az érintett modulok címkézése. */
  readonly moduleLabels: TModuleLabels
}
